import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import NotFoundPage from './NotFoundPage';
import { getOccasion } from '../data/occasions';

const BASE_URL = 'https://momentsent.com';

interface OccasionPageProps {
  /** Internal occasion slug, e.g. "birthday". Passed from the route. */
  slug: string;
}

/**
 * Occasion landing page, e.g. /birthday-cards
 *
 * Data-driven from web/src/data/occasions.ts. Renders only for occasions that
 * carry a populated `page` object; any other slug falls through to the 404.
 *
 * The copy is answer-first (AI search engines quote the intro), and the page
 * emits Service + FAQPage + BreadcrumbList JSON-LD so it is eligible for rich
 * results and AI citation. Internal links to /signup, /pricing, /designs, the
 * matching message guide, and the /occasions hub keep it well connected.
 */
export default function OccasionPage({ slug }: OccasionPageProps) {
  const occasion = getOccasion(slug);

  // Only occasions with full page content render here; everything else 404s.
  if (!occasion?.page) return <NotFoundPage />;

  const p = occasion.page;
  const canonical = `${BASE_URL}${p.path}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: p.h1,
      serviceType: `${occasion.occasion} card mailing service`,
      provider: {
        '@type': 'Organization',
        name: 'MomentSent',
        url: BASE_URL,
      },
      areaServed: 'Worldwide',
      description: p.intro,
      offers: {
        '@type': 'Offer',
        price: '7.99',
        priceCurrency: 'USD',
        description: 'Plans start at $7.99/month with a 14-day free trial and no credit card required.',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: p.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Occasions', item: `${BASE_URL}/occasions` },
        { '@type': 'ListItem', position: 3, name: occasion.occasion, item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title={p.metaTitle}
        description={p.metaDescription}
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            <span className="mr-2" aria-hidden="true">{occasion.emoji}</span>
            {occasion.occasion}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            {p.h1}
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            {p.intro}
          </p>
          <div className="mt-8">
            <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
              Start your free trial →
            </Link>
            <p className="mt-3 font-body text-xs text-gray-400">
              14-day free trial. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────────── */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-8 text-center">How it works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {p.steps.map((step, i) => (
              <div key={step.title} className="bg-white rounded-card p-6 shadow-warm border border-gray-100">
                <div className="font-heading text-3xl text-sage mb-3">{i + 1}</div>
                <h3 className="font-heading text-lg text-navy mb-2">{step.title}</h3>
                <p className="font-body text-sm text-gray-600 leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why MomentSent ──────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">
            Why send {occasion.occasion.toLowerCase()} cards with MomentSent
          </h2>
          <ul className="space-y-4">
            {p.benefits.map((b) => (
              <li key={b} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">✓</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Message guide teaser (optional) ─────────────────────────────────── */}
      {p.messageGuidePath && (
        <section className="py-10 px-6">
          <div className="max-w-3xl mx-auto bg-sage/10 rounded-card p-8 text-center">
            <h2 className="font-heading text-2xl text-navy mb-3">
              Not sure what to write?
            </h2>
            <p className="font-body text-base text-gray-600 leading-relaxed mb-6 max-w-xl mx-auto">
              Browse dozens of ready-to-use message ideas by tone and relationship, from heartfelt
              and sentimental to short and funny, so the inside of the card always feels right.
            </p>
            <Link to={p.messageGuidePath} className="font-body text-base text-sage font-semibold hover:underline">
              Read the message ideas guide →
            </Link>
          </div>
        </section>
      )}

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {p.faq.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Explore more / internal links ───────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-base text-gray-500 leading-relaxed">
            Explore <Link to="/designs" className="text-sage font-semibold hover:underline">card designs</Link>,
            {' '}see <Link to="/pricing" className="text-sage font-semibold hover:underline">plans and pricing</Link>,
            {' '}or browse every <Link to="/occasions" className="text-sage font-semibold hover:underline">occasion we mail cards for</Link>.
          </p>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            {p.ctaTitle ?? `Never miss a ${occasion.occasion.toLowerCase()} again.`}
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            {p.ctaBody ?? 'Set the date once and MomentSent mails a real card automatically, every year, to the people you love.'}
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
